import { useTranslations } from 'next-intl';
import { type FC, type KeyboardEvent } from 'react';

import clsx from 'clsx';

import {
  type RevenueChartCategory,
  type RevenueChartCategoryData,
} from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/hooks/use-revenue-chart-data';
import { CornerBorders } from '@/components/common/CornerBorders';
import { Flex } from '@/components/common/Flex';
import { ArrowDownIcon } from '@/components/common/icons/ArrowDownIcon';
import { ArrowUpIcon } from '@/components/common/icons/ArrowUpIcon';
import { OverflowingTextTip } from '@/components/common/OverflowingTextTip';
import { Text } from '@/components/common/Text';
import { useGetCurrencyIcon } from '@/hooks/use-get-currency-icon';
import { type WithClassName } from '@/types/react';
import { formatRevenueComparisonPeriodLabel, formatRevenuePeriodRange } from '@/utils/date';
import { formatAmount, formatPercent } from '@/utils/number';

import styles from './Category.module.scss';

const PLACEHOLDER_COMPARISON_PERIOD = formatRevenuePeriodRange('2026-01-01', '2026-12-31') ?? '';

type CategoryProps = WithClassName & {
  category: RevenueChartCategory;
  categoryData: RevenueChartCategoryData;
  isSelected: boolean;
  currency: string;
  onClick: () => void;
  isPlaceholder?: boolean;
  withComparisonPlaceholder?: boolean;
};

export const Category: FC<CategoryProps> = (props) => {
  const t = useTranslations('pages.dashboard.widgets.revenue');
  const tCategories = useTranslations('pages.dashboard.widgets.revenue.categories');

  const {
    className,
    category,
    categoryData,
    isSelected,
    currency,
    onClick,
    isPlaceholder = false,
    withComparisonPlaceholder = false,
  } = props;

  const getCurrencyIcon = useGetCurrencyIcon();

  const comparisonPeriodText =
    formatRevenuePeriodRange(categoryData.comparison_period_start, categoryData.comparison_period_end) ??
    (categoryData.comparison_period_label
      ? formatRevenueComparisonPeriodLabel(categoryData.comparison_period_label)
      : null);

  const showComparison = Boolean(comparisonPeriodText) || (isPlaceholder && withComparisonPlaceholder);
  const isPositive = categoryData.percent_change > 0;
  const isNegative = categoryData.percent_change < 0;
  const Icon = isPositive ? ArrowUpIcon : isNegative ? ArrowDownIcon : null;
  const accentColor = isPositive ? 'green' : isNegative ? 'red-40p' : 'white-50p';
  const mainValueColor = isSelected ? 'white' : accentColor;

  return (
    <CornerBorders
      isActive={isSelected}
      className={clsx(
        styles.root,
        isPositive && styles.positive,
        isNegative && styles.negative,
        { [styles.isSelected]: isSelected },
        isPlaceholder && styles.placeholder,
        className
      )}
      aria-hidden={isPlaceholder}
      aria-pressed={isPlaceholder ? undefined : isSelected}
      onClick={isPlaceholder ? undefined : onClick}
      role={isPlaceholder ? 'presentation' : 'button'}
      tabIndex={isPlaceholder ? -1 : 0}
      onKeyDown={
        isPlaceholder
          ? undefined
          : (e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
      }
    >
      <div className={styles.box}>
        <Text className={styles.title} type="regular14" color="white-50p">
          {tCategories(category)}
        </Text>

        <OverflowingTextTip
          className={clsx(styles.mainValue, isPlaceholder && styles.placeholderHidden)}
          type="medium18"
          align="left"
          color={mainValueColor}
        >
          {category === 'amount'
            ? categoryData.main_value
            : formatAmount(categoryData.main_value, getCurrencyIcon(currency) ?? currency)}
        </OverflowingTextTip>

        {showComparison && (
          <div className={styles.comparison}>
            {isPlaceholder && !comparisonPeriodText ? (
              <>
                <Flex className={styles.comparisonChange} gap={1} alignItems="center" wrap="nowrap" aria-hidden>
                  <span className={styles.placeholderHidden}>
                    <ArrowUpIcon size={14} />
                  </span>
                  <Text className={clsx(styles.comparisonPercent, styles.placeholderHidden)} type="regular12">
                    +0%
                  </Text>
                </Flex>

                <Text className={clsx(styles.comparisonPeriod, styles.placeholderHidden)} type="regular12">
                  {t('comparedToLabel', { period: PLACEHOLDER_COMPARISON_PERIOD })}
                </Text>
              </>
            ) : (
              comparisonPeriodText && (
                <>
                  <Flex className={styles.comparisonChange} gap={1} alignItems="center" wrap="nowrap">
                    {Icon && <Icon size={14} color={accentColor} />}

                    <Text className={styles.comparisonPercent} type="regular12" color={accentColor}>
                      {formatPercent(categoryData.percent_change / 100)}
                    </Text>
                  </Flex>

                  <Text className={styles.comparisonPeriod} type="regular12" color="white-32p">
                    {t('comparedToLabel', { period: comparisonPeriodText })}
                  </Text>
                </>
              )
            )}
          </div>
        )}
      </div>
    </CornerBorders>
  );
};
